import React, { useCallback, useEffect, useRef } from 'react';
import { videoContentRect, toNormalized, fromNormalized } from '../../utils/videoContentRect';

// Shared pointing and drawing over a screen share.
//
// Rendered as a sibling of the <video> inside the screen-share tile, sized to
// the tile but painting only within the picture (see videoContentRect.js -
// object-fit:contain means the two are rarely the same rectangle).
//
// Everything here rides the same data channel as chat and reactions, so it
// works for a guest with no account exactly as it does for the host. Nothing
// is persisted: a late joiner sees whatever is drawn from the moment they
// arrive, which matches how a whiteboard behaves in a room you walk into.

// One batch per frame at most. Sending a message per pointermove would flood
// the channel at the mouse's sample rate, which on a high-polling-rate mouse
// is several hundred events a second.
const FLUSH_INTERVAL_MS = 50;
const LINE_WIDTH_RATIO = 0.0035; // of the picture's width, so a stroke scales with the share

export default function MeetingAnnotations({
    videoRef, drawing, color, annotations, onStroke, onPointer, localIdentity
}) {
    const canvasRef = useRef(null);
    // Points gathered since the last flush, plus the id of the stroke in
    // progress. A stroke is one pen-down to pen-up.
    const pendingRef = useRef({ strokeId: null, points: [] });
    const frameRef = useRef(null);

    // ===== Painting =====

    const paint = useCallback(() => {
        frameRef.current = null;
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video) return;

        const rect = videoContentRect(video);
        if (!rect) return;

        // Backing store at device resolution, CSS box at layout size -
        // otherwise strokes are visibly soft on any HiDPI screen.
        const dpr = window.devicePixelRatio || 1;
        const width = video.clientWidth;
        const height = video.clientHeight;
        if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
        }

        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = Math.max(2, rect.width * LINE_WIDTH_RATIO);

        annotations.strokes.forEach((stroke) => {
            if (stroke.pts.length === 0) return;
            ctx.strokeStyle = stroke.color || '#fff';
            ctx.beginPath();
            stroke.pts.forEach((point, index) => {
                const { x, y } = fromNormalized(rect, point[0], point[1]);
                if (index === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
            // A single-point stroke is a tap, not a line - stroke() would draw
            // nothing at all for it.
            if (stroke.pts.length === 1) {
                const { x, y } = fromNormalized(rect, stroke.pts[0][0], stroke.pts[0][1]);
                ctx.arc(x, y, ctx.lineWidth / 2, 0, Math.PI * 2);
                ctx.fillStyle = stroke.color || '#fff';
                ctx.fill();
            } else {
                ctx.stroke();
            }
        });

        // Live pointers on top of the ink, each labelled - "who is pointing at
        // what" is the whole question this answers.
        Object.entries(annotations.pointers).forEach(([identity, pointer]) => {
            if (identity === localIdentity) return; // your own cursor is already on screen
            const { x, y } = fromNormalized(rect, pointer.x, pointer.y);
            ctx.fillStyle = pointer.color || '#fff';
            ctx.beginPath();
            ctx.arc(x, y, 7, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = 'rgba(0,0,0,0.55)';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.font = '500 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            const label = pointer.name || '';
            const padding = 5;
            const textWidth = ctx.measureText(label).width;
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillRect(x + 11, y - 9, textWidth + padding * 2, 18);
            ctx.fillStyle = '#fff';
            ctx.fillText(label, x + 11 + padding, y + 4);

            ctx.lineWidth = Math.max(2, rect.width * LINE_WIDTH_RATIO);
        });
    }, [annotations, localIdentity, videoRef]);

    // Coalesced to one paint per frame: several strokes and pointers can
    // change within a single tick, and each would otherwise trigger its own
    // full redraw.
    useEffect(() => {
        if (frameRef.current) return undefined;
        frameRef.current = requestAnimationFrame(paint);
        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
        };
    }, [paint]);

    // The canvas is sized from the video, so a window resize (or the side
    // panel opening) has to repaint even though no annotation changed.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;
        const observer = new ResizeObserver(() => paint());
        observer.observe(video);
        return () => observer.disconnect();
    }, [paint, videoRef]);

    // ===== Input =====

    useEffect(() => {
        if (!drawing) return undefined;
        const interval = setInterval(() => {
            const pending = pendingRef.current;
            if (!pending.strokeId || pending.points.length === 0) return;
            onStroke(pending.strokeId, color, pending.points);
            pending.points = [];
        }, FLUSH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [drawing, color, onStroke]);

    const pointFromEvent = (event) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const bounds = canvas.getBoundingClientRect();
        return toNormalized(videoRef.current, event.clientX - bounds.left, event.clientY - bounds.top);
    };

    const handlePointerDown = (event) => {
        if (!drawing) return;
        const point = pointFromEvent(event);
        if (!point) return;
        // Capture, so a stroke that wanders off the tile mid-drag still ends
        // cleanly instead of leaving the pen stuck down.
        event.currentTarget.setPointerCapture(event.pointerId);
        pendingRef.current = { strokeId: `${localIdentity}-${Date.now()}`, points: [[point.x, point.y]] };
    };

    const handlePointerMove = (event) => {
        const point = pointFromEvent(event);
        if (!point) return;
        // Pointing is always on; drawing only adds ink. Someone who just wants
        // to say "look at this" should not have to pick up a tool first.
        onPointer(point.x, point.y, color);
        if (!drawing || !pendingRef.current.strokeId) return;
        pendingRef.current.points.push([point.x, point.y]);
    };

    const endStroke = (event) => {
        const pending = pendingRef.current;
        if (pending.strokeId && pending.points.length > 0) {
            onStroke(pending.strokeId, color, pending.points);
        }
        pendingRef.current = { strokeId: null, points: [] };
        if (event && event.currentTarget.hasPointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    return (
        <canvas
            ref={canvasRef}
            className={`MeetAnnotationCanvas ${drawing ? 'MeetAnnotationCanvasDrawing' : ''}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onPointerLeave={() => endStroke(null)}
        />
    );
}
